import {
  DOCUMENT,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { environment } from '@environments/environment';

type MockFunc = (...args: unknown[]) => Promise<string>;

declare const turnstile: { ready: MockFunc; render: MockFunc };

@Injectable({ providedIn: 'root' })
export class TurnstileService {
  public isLoaded = false;
  public token: string = '';
  private readonly TURNSTILE_SCRIPT =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  private readonly document: Document = inject(DOCUMENT);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  /**
   * Load CloudFlare Turnstile script
   *
   * @param turnstileKey Turnstile key
   */
  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) resolve();

      const script: HTMLScriptElement = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.src = `${this.TURNSTILE_SCRIPT}`;
      script.onload = (): void => {
        this.isLoaded = true;
        resolve();
      };
      script.onerror = (error): void => {
        reject(error);
      };
      this.renderer.appendChild(this.document.head, script);
    });
  }

  /**
   * Get CloudFlare Turnstile Token
   *
   * @param turnstileKey turnstile key
   * @param env Environment Type
   * @param action handled action
   * @returns {Promise<string>} token
   */
  async getToken(container: HTMLElement): Promise<string> {
    return new Promise((resolve, reject) => {
      this.loadScript().then(() => {
        if (!environment.production) resolve('');

        if (!this.isLoaded) reject('Turnstile script not loaded');

        turnstile.ready(() => {
          turnstile.render(container, {
            sitekey: environment.cloudFlareTurnstileKey,
            callback: (token: string) => {
              this.token = token;
            },
          });
        });
      });
    });
  }
}
